import { useMutation, useQuery } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { throwForValidationResult } from "@/lib/graphql-error";
import {
  CefrScale,
  CreateLanguageSkillDocument,
  DeleteLanguageSkillDocument,
  MyselfExpertiseDataDocument,
  UpdateLanguageSkillDocument,
  type MyselfExpertiseDataQuery,
} from "@/graphql/generated/operations";
import { recipientRelations, type ContentRecipient } from "@/lib/content-sharing";

export interface LanguageFormInput {
  languageTag: string;
  cefr: string;
  nativeSpeaker: boolean;
  selfAssessed: boolean;
  sharedWith?: ReadonlyArray<ContentRecipient | string>;
  publiclyReadable?: boolean;
  allGroups?: boolean;
}

export type LanguageItem = NonNullable<
  NonNullable<MyselfExpertiseDataQuery["myself"]>["languageSkills"]
>[number];

export interface UseLanguagesResult {
  items: readonly LanguageItem[];
  add: (input: LanguageFormInput) => Promise<void>;
  update: (id: string, input: LanguageFormInput) => Promise<void>;
  remove: (id: string) => Promise<void>;
  busy: boolean;
  error: Error | null;
}

function toCefr(v: string): CefrScale | null {
  if (v === "") return null;
  return (Object.values(CefrScale) as string[]).includes(v) ? (v as CefrScale) : null;
}

function toDataInput(input: LanguageFormInput) {
  return {
    languageTag: input.languageTag,
    // A native speaker has no foreign-language proficiency level: never
    // persist a CEFR rung alongside nativeSpeaker, whatever the form held.
    cefr: input.nativeSpeaker ? null : toCefr(input.cefr),
    nativeSpeaker: input.nativeSpeaker,
    selfAssessed: input.selfAssessed,
    ...(input.allGroups !== undefined ? { allGroups: input.allGroups } : {}),
    ...(input.publiclyReadable !== undefined ? { publiclyReadable: input.publiclyReadable } : {}),
    ...(input.sharedWith !== undefined
      ? { sharedWith: recipientRelations(input.sharedWith) }
      : {}),
  };
}

export function useLanguages(): UseLanguagesResult {
  const intl = useIntl();
  const query = useQuery(MyselfExpertiseDataDocument);
  const personId = query.data?.myself?.id;

  const saveFailed = intl.formatMessage({ id: "profile.page.error.saveLanguage", defaultMessage: "Could not save the language" });

  const [createMut, createState] = useMutation(CreateLanguageSkillDocument, {
    refetchQueries: [{ query: MyselfExpertiseDataDocument }],
  });
  const [updateMut, updateState] = useMutation(UpdateLanguageSkillDocument, {
    refetchQueries: [{ query: MyselfExpertiseDataDocument }],
  });
  const [deleteMut, deleteState] = useMutation(DeleteLanguageSkillDocument, {
    refetchQueries: [{ query: MyselfExpertiseDataDocument }],
  });

  async function add(input: LanguageFormInput) {
    if (!personId) throw new Error("useLanguages: no personId yet");
    const created = await createMut({
      variables: {
        input: { data: { ...toDataInput(input), person: { id: personId } } },
      },
    });
    // Both union members are selected, so a rejection resolves rather than throws. Fail-closed on
    // the success typename so an unexpected member is a failure, not a silent "saved".
    const out = created.data?.createLanguageSkill;
    if (out?.__typename !== "CreateLanguageSkillResult") {
      throwForValidationResult(out, saveFailed);
    }
  }

  async function update(id: string, input: LanguageFormInput) {
    const updated = await updateMut({ variables: { id, input: { data: toDataInput(input) } } });
    // Both union members are selected, so a rejection resolves rather than throws. Fail-closed on
    // the success typename so an unexpected member is a failure, not a silent "saved".
    const out = updated.data?.updateLanguageSkill;
    if (out?.__typename !== "UpdateLanguageSkillResult") {
      throwForValidationResult(out, saveFailed);
    }
  }

  async function remove(id: string) {
    await deleteMut({ variables: { input: { id } } });
  }

  return {
    items: query.data?.myself?.languageSkills ?? [],
    add,
    update,
    remove,
    busy: createState.loading || updateState.loading || deleteState.loading,
    error:
      (createState.error as Error | undefined) ??
      (updateState.error as Error | undefined) ??
      (deleteState.error as Error | undefined) ??
      null,
  };
}
